import React from "react";
import { useTranslation, Trans } from "react-i18next";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Trans component maps are pure static JSX; module-scope so re-renders don't
// churn identities and re-run Trans reconciliation. (iter55 hoist.)
const TRANS_COMPONENTS_UNDERLINE_RUST = { u: <u className="text-rust font-semibold" /> };
const TRANS_COMPONENTS_BOLD_UNDERLINE_RUST = { b: <b />, u: <u className="text-rust font-semibold" /> };
const TRANS_COMPONENTS_BOLD = { b: <b /> };

const speciesKey = (it) => {
  const sci = (it?.identification?.scientific_name || "").trim().toLowerCase();
  if (sci) return `s:${sci}`;
  const cn = (it?.identification?.common_name || "").trim().toLowerCase();
  return cn ? `c:${cn}` : null;
};

const SpeciesMismatchWarning = ({ items, selected }) => {
  const { t } = useTranslation();
  // Detect species mismatch across the selection. We match on
  // scientific_name (case-insensitive) so common-name variations
  // ("Purple Coneflower" vs "Coneflower") on the same species
  // don't false-positive. When no scientific name is present we
  // fall back to common_name. Empty strings are ignored.
  const chosen = (items || []).filter((i) => selected.has(i.scan_id));
  const distinct = new Set(chosen.map(speciesKey).filter(Boolean));
  if (distinct.size <= 1) return null;
  const chronological = [...chosen].sort((a, b) =>
    String(a.created_at || "").localeCompare(String(b.created_at || ""))
  );
  const primaryName =
    chronological[0]?.identification?.common_name
    || chronological[0]?.identification?.scientific_name
    || t("gallery.card.unknown");
  return (
    <div
      data-testid="bulk-merge-species-warning"
      className="mt-1 rounded-xl border border-[#E9C46A]/50 bg-[#FFF4DC] px-3 py-2.5 text-xs text-[#8a6a14] leading-relaxed"
      role="note"
      aria-live="polite"
    >
      <span className="font-semibold">{t("gallery.mergeSpeciesWarnTitle")}: </span>
      <Trans
        i18nKey="gallery.mergeSpeciesWarnBody"
        values={{ n: distinct.size, name: primaryName }}
        components={TRANS_COMPONENTS_BOLD}
      />
    </div>
  );
};

/**
 * All four Gallery.jsx confirmation dialogs collected into one component so
 * the parent stays focused on state + list rendering. Behavior-preserving
 * extraction — all data-testid values, styles and copy keys unchanged.
 */
export const GalleryDialogs = ({
  // Single-delete
  pendingDelete,
  onOpenChangeDelete,
  onConfirmDelete,
  // Bulk-delete
  pendingBulkDelete,
  onOpenChangeBulkDelete,
  onConfirmBulkDelete,
  // Bulk-merge
  pendingBulkMerge,
  onOpenChangeBulkMerge,
  onConfirmBulkMerge,
  merging,
  // Mark-reviewed
  pendingMarkReviewed,
  onOpenChangeMarkReviewed,
  onConfirmMarkReviewed,
  // Shared context for the species-warning inside bulk-merge
  items,
  selected,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {/* Single-delete confirmation */}
      <AlertDialog open={!!pendingDelete} onOpenChange={onOpenChangeDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{t("gallery.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              <Trans i18nKey="gallery.deleteDesc" components={TRANS_COMPONENTS_UNDERLINE_RUST} />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm" onClick={onConfirmDelete} className="bg-rust hover:bg-rust/90 font-bold uppercase tracking-wide">
              {t("gallery.deleteConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk-delete confirmation */}
      <AlertDialog open={pendingBulkDelete} onOpenChange={onOpenChangeBulkDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{t("gallery.bulkDeleteTitle", { n: selected.size })}</AlertDialogTitle>
            <AlertDialogDescription>
              <Trans i18nKey="gallery.bulkDeleteDesc" components={TRANS_COMPONENTS_UNDERLINE_RUST} />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="bulk-delete-confirm" onClick={onConfirmBulkDelete} className="bg-rust hover:bg-rust/90 font-bold uppercase tracking-wide">
              {t("gallery.bulkDeleteConfirm", { n: selected.size })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk-merge confirmation (plain path — the photo-picker path uses
          MergePickerDialog and is rendered separately in the parent). */}
      <AlertDialog open={pendingBulkMerge} onOpenChange={onOpenChangeBulkMerge}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display" data-testid="bulk-merge-title">
              {t("gallery.bulkMergeTitle", { n: selected.size })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans i18nKey="gallery.bulkMergeDesc" components={TRANS_COMPONENTS_BOLD_UNDERLINE_RUST} />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <SpeciesMismatchWarning items={items} selected={selected} />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-merge-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="bulk-merge-confirm" onClick={() => onConfirmBulkMerge()} disabled={merging} className="bg-leaf hover:bg-leaf-deep">
              {merging ? t("gallery.merging") : t("gallery.mergeConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mark-reviewed confirmation */}
      <AlertDialog open={!!pendingMarkReviewed} onOpenChange={onOpenChangeMarkReviewed}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display" data-testid="mark-reviewed-title">
              {t("gallery.markReviewedTitle", { n: pendingMarkReviewed?.ids?.length || 0 })}
            </AlertDialogTitle>
            <AlertDialogDescription>{t("gallery.markReviewedDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mark-reviewed-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="mark-reviewed-confirm" onClick={onConfirmMarkReviewed} className="bg-leaf hover:bg-leaf-deep">
              {t("gallery.markReviewedConfirm", { n: pendingMarkReviewed?.ids?.length || 0 })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
